import { useEffect, useCallback, useMemo } from 'react';

import { useCurrentProject } from '@adsk/alloy-provider';

import { UseEntitiesCacheReturnType } from './useEntitiesCache';
import useMemoizedStringArray from './useMemoizedStringArray';
import usePendingIds from './usePendingIds';
import { useEntitiesCacheContext } from '../providers/EntitiesCacheProvider';
import { CommonReferencePickerPlugin } from '../types/ReferencePickerPlugin';

const PAGE_SIZE = 100;

export default (plugin: CommonReferencePickerPlugin, idsArg: string[]) => {
  const { entitiesCache, addEntitiesToCache, addLoadingIds, addErrorIds } =
    useEntitiesCacheContext();
  const currentProject = useCurrentProject();
  const projectId = currentProject?.id;
  const projectScope = currentProject?.scopes?.project;
  const fetchedEntityIds = useMemo(() => {
    const { type, domain } = plugin.metadata;
    const cache = entitiesCache?.[domain]?.[type];
    return cache ? Object.keys(cache) : [];
  }, [plugin, entitiesCache]);
  const memoizedFetchedEntityIds = useMemoizedStringArray(fetchedEntityIds);
  const {
    pendingIdsArray,
    addPendingIds,
    addAlreadyFetchedIds,
    clearPendingIds,
  } = usePendingIds();

  useEffect(() => {
    if (memoizedFetchedEntityIds) {
      addAlreadyFetchedIds(memoizedFetchedEntityIds);
    }
  }, [addAlreadyFetchedIds, memoizedFetchedEntityIds]);

  const memoizedIds = useMemoizedStringArray(idsArg);
  useEffect(() => {
    addPendingIds(memoizedIds);
  }, [addPendingIds, memoizedIds]);

  const readData = useCallback(
    (
      projectId: string,
      projectScope: string[] | undefined,
      plugin: CommonReferencePickerPlugin,
      ids: string[],
      addLoadingIds: UseEntitiesCacheReturnType['addLoadingIds'],
      addEntitiesToCache: UseEntitiesCacheReturnType['addEntitiesToCache'],
      addErrorIds: UseEntitiesCacheReturnType['addErrorIds'],
    ) => {
      if (ids.length) {
        const { type, domain } = plugin.metadata;
        addLoadingIds({ ids, domain, type });
        plugin
          .getEntitiesByIds({ ids, projectId, projectScope })
          .then(({ data: entities }) => {
            addEntitiesToCache({ entities, requestIds: ids, domain, type });
          })
          .catch((error) => {
            console.error(
              'ReferencePicker - useGetEntities: Error getting entities',
              error,
            );
            // This is needed as we hold error per entity, and we might need to trigger retry for multiple entities.
            // So here we mark whether the retry method was called or not.
            let retryCalled = false;
            addErrorIds({
              ids,
              domain,
              type,
              error,
              retry: () => {
                if (!retryCalled) {
                  readData(
                    projectId,
                    projectScope,
                    plugin,
                    ids,
                    addLoadingIds,
                    addEntitiesToCache,
                    addErrorIds,
                  );
                  retryCalled = true;
                }
              },
            });
          });
      }
    },
    [],
  );

  useEffect(() => {
    if (projectId && pendingIdsArray.length) {
      for (let i = 0; i < pendingIdsArray.length; i += PAGE_SIZE) {
        const chunkIds = pendingIdsArray.slice(i, i + PAGE_SIZE);
        readData(
          projectId,
          projectScope,
          plugin,
          chunkIds,
          addLoadingIds,
          addEntitiesToCache,
          addErrorIds,
        );
      }
      clearPendingIds();
    }
  }, [
    readData,
    pendingIdsArray,
    plugin,
    projectId,
    projectScope,
    addLoadingIds,
    addEntitiesToCache,
    addErrorIds,
    clearPendingIds,
  ]);

  return {
    fetchByIds: addPendingIds,
  };
};
